import React from 'react';
import * as yup from 'yup';
import {QuickSettingsForm, FormFieldConfig} from '@/components/forms';
import {useAlert} from '@/components/alerts';
import useData from '@/utils/useData';
import {buildURL} from '@/utils/urlBuilder';
import CellLoader from '@/components/loading/CellLoader';
import i18n from 'i18n';
import {useStoreActions} from 'easy-peasy';

interface Dictionary {
    id: number;
    context: string;
    jsonDataString: string;
    typeId: number | null;
    eventId: number | null;
    lang: string;
    active: boolean;
}

interface SelectOption {
    value: number | string;
    label: string;
}

interface DictionarySettingsFormProps {
    dictionaryId?: number;
    onSave?: () => void;
}

interface FormValues {
    context: string;
    jsonDataString: string;
    typeId: number | null;
    eventId: number | null;
    lang: string;
    active: boolean;
}


const DictionarySettingsForm: React.FC<DictionarySettingsFormProps> = React.memo((
    {
        dictionaryId = 0,
        onSave = () => {},
    },
) => {
    const {showSuccess} = useAlert();
    const setReloadDictionaries = useStoreActions((actions: any) => actions.dictionaries.setReload);
    const [langFieldChoices, langFieldChoicesLoading] = useData<SelectOption[]>(buildURL('langFieldChoicesAll'), 'langFieldChoices');
    const [eventTypeFieldChoices, eventTypeFieldChoicesLoading] = useData<SelectOption[]>(window.URLS.eventTypeFieldChoices, 'eventTypeFieldChoices');
    const [dictionaryTypeFieldChoices, dictionaryTypeFieldChoicesLoading] = useData<SelectOption[]>(window.URLS.dictionaryTypeFieldChoices, 'dictionaryTypeFieldChoices');


    const initialValues: FormValues = {
        context: '',
        jsonDataString: '',
        typeId: null,
        eventId: null,
        lang: '',
        active: true,
    };

    const customSuccessHandling = (response: any, actionName: string) => {
        if (dictionaryId) {
            showSuccess(i18n.t('jt_dict_lexicon_changed.text'));
        } else {
            showSuccess(i18n.t('jt_dict_lexicon_created.text'));
        }
        
        // Immediately trigger list reload
        setReloadDictionaries(true);
        
        // Call the onSave callback
        onSave();
    };

    const fields: FormFieldConfig[] = [
        {
            name: 'context',
            label: i18n.t('jt_lexicon_name.text'),
            type: 'text',
            required: true,
            validation: yup.string()
                .max(100, i18n.t('jt_dict_context_max.text'))
                .required(i18n.t('jt_dict_context_required.text')),
        },
        {
            name: 'eventId',
            label: i18n.t('jt_select_event.text'),
            type: 'select',
            required: true,
            options: eventTypeFieldChoices || [],
            validation: yup.number().required(i18n.t('jt_dict_event_required.text')),
        },
        {
            name: 'lang',
            label: i18n.t('jt_select_language.text'),
            type: 'select',
            required: true,
            options: langFieldChoices || [],
            validation: yup.string().required(i18n.t('jt_dict_lang_required.text')),
        },
        {
            name: 'typeId',
            label: i18n.t('jt_select_the_parameter_type.text'),
            type: 'select',
            required: true,
            options: dictionaryTypeFieldChoices || [],
            validation: yup.number().required(i18n.t('jt_dict_type_required.text')),
        },
        {
            name: 'jsonDataString',
            label: i18n.t('jt_words.text'),
            type: 'textarea',
            required: true,
            validation: yup.string().required(i18n.t('jt_dict_words_required.text')),
        },
        {
            name: 'active',
            label: i18n.t('es_active.text'),
            type: 'checkbox',
            required: true,
            validation: yup.boolean().required(i18n.t('jt_dict_active_required.text')),
        },
    ];


    // Wait for all select options to load before rendering the form
    if (langFieldChoicesLoading || eventTypeFieldChoicesLoading || dictionaryTypeFieldChoicesLoading) {
        return <CellLoader fullHeight={true} />;
    }

    return (
        <QuickSettingsForm
            entityId={dictionaryId || undefined}
            crudConfig={{
                resourceUrl: window.URLS.dictionariesResource,
                storeActionPath: 'dictionaries.addOrEdit',
                successMessageKey: 'jt_dict_lexicon_changed.text',
                entityName: 'dictionary'
            }}
            fields={fields}
            initialValues={initialValues}
            onSave={() => {}} // Empty function since we handle onSave in customSuccessHandling
            customSuccessHandling={customSuccessHandling}
        />
    );
});


export default DictionarySettingsForm; 